import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { ClasseComposant, ClassesComposantsService } from '../../../services/classes-composants.service';
import { Composant, ComposantPayload, ComposantsService } from '../../../services/composants.service';

@Component({
  selector: 'app-modifier-composant',
  imports: [CommonModule, FormsModule],
  templateUrl: './modifier-composant.component.html',
  styleUrl: './modifier-composant.component.scss'
})
export class ModifierComposantComponent implements OnInit, OnDestroy {
  protected composants: Composant[] = [];
  protected classesComposants: ClasseComposant[] = [];
  protected selectedId = '';
  protected selectedClasseId = '';

  protected nom = '';
  protected lien = '';
  protected prix = '';
  protected standardEmballage = '';
  protected unite = '';
  protected refFabricant = '';
  protected commentaire = '';
  protected imageFile: File | null = null;
  protected existingPhotoPreview = '';
  protected selectedPhotoPreview = '';

  protected loading = false;
  protected loadingComposants = false;
  protected loadingClasses = false;
  protected success = '';
  protected error = '';
  protected toastMessage = '';
  protected toastType: 'success' | 'error' | '' = '';

  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingSelectedId: number | null = null;
  private readonly apiBaseUrl = environment.apiBaseUrl;

  constructor(
    private readonly composantsService: ComposantsService,
    private readonly classesComposantsService: ClassesComposantsService,
    private readonly route: ActivatedRoute,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params: Params) => {
      const idParam = Number(params['id']);
      this.pendingSelectedId = Number.isFinite(idParam) && idParam > 0 ? idParam : null;
      if (this.pendingSelectedId && this.composants.length > 0) {
        this.selectedId = String(this.pendingSelectedId);
        this.onSelectChange();
      }
    });

    this.loadComposants();
    this.loadClassesComposants();

    setTimeout(() => {
      if (this.loadingComposants && this.composants.length === 0) {
        void this.loadComposantsWithFetchFallback();
      }
    }, 2500);
  }

  protected onSelectChange(): void {
    const selected = this.composants.find((c) => c.id === Number(this.selectedId));
    if (!selected) {
      this.existingPhotoPreview = '';
      this.selectedPhotoPreview = '';
      return;
    }

    this.nom = selected.nom;
    this.lien = selected.lien ?? '';
    this.prix = selected.prix != null ? String(selected.prix) : '';
    this.standardEmballage = selected.standard_emballage ?? '';
    this.unite = selected.unite ?? '';
    this.refFabricant = selected.ref_fabricant ?? '';
    this.commentaire = selected.commentaire ?? '';
    this.selectedClasseId = selected.classe_id != null ? String(selected.classe_id) : '';
    this.imageFile = null;
    this.selectedPhotoPreview = '';
    this.existingPhotoPreview = this.normalizePhotoUrl(selected.photo);
  }

  protected async onFileChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    this.imageFile = input.files?.[0] ?? null;

    if (!this.imageFile) {
      this.selectedPhotoPreview = '';
      return;
    }

    try {
      this.selectedPhotoPreview = await this.readFileAsDataUrl(this.imageFile);
    } catch {
      this.selectedPhotoPreview = '';
    }
  }

  protected get imagePreviewToDisplay(): string {
    return this.selectedPhotoPreview || this.existingPhotoPreview;
  }

  protected getPrixUnitaire(): string {
    if (!this.prix || !this.standardEmballage) {
      return '-';
    }

    const prixNum = parseFloat(this.prix);
    const standardNum = parseFloat(this.standardEmballage);

    if (isNaN(prixNum) || isNaN(standardNum) || standardNum <= 0) {
      return '-';
    }

    const prixUnitaire = prixNum / standardNum;
    return prixUnitaire.toFixed(2);
  }

  protected async submit(): Promise<void> {
    this.error = '';
    this.success = '';

    const id = Number(this.selectedId);
    if (!id) {
      this.error = 'Sélectionne un composant à modifier.';
      this.showToast(this.error, 'error');
      return;
    }

    if (!this.nom.trim()) {
      this.error = 'Le nom est requis.';
      this.showToast(this.error, 'error');
      return;
    }

    if (this.prix && !this.isValidPrice(this.prix)) {
      this.error = 'Le prix doit être un nombre avec au maximum 2 décimales.';
      this.showToast(this.error, 'error');
      return;
    }

    if (this.unite && this.unite.trim().length > 3) {
      this.error = "L'unité ne doit pas dépasser 3 caractères.";
      this.showToast(this.error, 'error');
      return;
    }

    const classeId = Number(this.selectedClasseId);
    if (!classeId) {
      this.error = 'La classe du composant est obligatoire.';
      this.showToast(this.error, 'error');
      return;
    }

    const payload: ComposantPayload = {
      nom: this.nom.trim(),
      lien: this.lien,
      prix: this.prix != null && this.prix !== '' ? Number(this.prix) : null,
      standard_emballage: this.standardEmballage,
      unite: this.unite,
      ref_fabricant: this.refFabricant,
      commentaire: this.commentaire,
      classe_id: String(classeId),
    };

    if (this.imageFile) {
      try {
        payload.photo = await this.resizeImageToBase64(this.imageFile, 600);
      } catch {
        this.error = "Impossible de traiter l'image sélectionnée.";
        this.showToast(this.error, 'error');
        return;
      }
    }

    this.loading = true;
    this.composantsService.update(id, payload).subscribe({
      next: (updated: Composant) => {
        this.zone.run(() => {
          this.loading = false;
          this.success = `Composant modifié : ${updated.nom}`;
          this.imageFile = null;
          this.selectedPhotoPreview = '';
          this.existingPhotoPreview = this.normalizePhotoUrl(updated.photo);
          this.showToast(this.success, 'success');
          this.loadComposants();
          this.cdr.detectChanges();
        });
      },
      error: (err: unknown) => {
        this.zone.run(() => {
          this.loading = false;
          this.error = this.extractErrorMessage(err, 'Modification impossible.');
          this.showToast(this.error, 'error');
          this.cdr.detectChanges();
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }

  private loadComposants(): void {
    this.loadingComposants = true;
    this.composantsService.list().subscribe({
      next: (items: Composant[]) => {
        this.loadingComposants = false;
        this.composants = Array.isArray(items) ? items : [];
        if (this.pendingSelectedId) {
          const exists = this.composants.some((c) => c.id === this.pendingSelectedId);
          if (exists) {
            this.selectedId = String(this.pendingSelectedId);
            this.onSelectChange();
          }
        }
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        this.loadingComposants = false;
        this.error = this.extractErrorMessage(err, 'Impossible de charger les composants.');
        this.cdr.detectChanges();
      }
    });
  }

  private async loadComposantsWithFetchFallback(): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/composants`, { cache: 'no-store' });
      if (!response.ok) {
        return;
      }

      const payload = (await response.json()) as { items?: Composant[] } | Composant[];
      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

      this.zone.run(() => {
        this.composants = items;
        this.loadingComposants = false;
        this.cdr.detectChanges();
      });
    } catch {
      // Keep current state if fallback fails.
    }
  }

  private loadClassesComposants(): void {
    this.loadingClasses = true;
    this.classesComposantsService.list().subscribe({
      next: (items: ClasseComposant[]) => {
        this.loadingClasses = false;
        this.classesComposants = Array.isArray(items) ? items : [];
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        this.loadingClasses = false;
        this.error = this.extractErrorMessage(err, 'Impossible de charger les classes composants.');
        this.cdr.detectChanges();
      }
    });
  }

  private isValidPrice(value: unknown): boolean {
    const normalized = String(value ?? '').trim();
    return /^\d+(\.\d{1,2})?$/.test(normalized);
  }

  private isValidPositiveInteger(value: unknown): boolean {
    const normalized = String(value ?? '').trim();
    return /^[1-9]\d*$/.test(normalized);
  }

  private resizeImageToBase64(file: File, maxDimension: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = () => reject(new Error('read_error'));
      reader.onload = async () => {
        const src = String(reader.result ?? '');
        if (!src) {
          reject(new Error('empty_source'));
          return;
        }

        try {
          const image = await this.loadImageFromDataUrl(src);
          const sourceWidth = image.width;
          const sourceHeight = image.height;
          if (!sourceWidth || !sourceHeight) {
            reject(new Error('invalid_dimensions'));
            return;
          }

          const inputType = (file.type || '').toLowerCase();
          const orientation =
            inputType === 'image/jpeg' || inputType === 'image/jpg'
              ? this.getJpegExifOrientation(await this.readFileAsArrayBuffer(file))
              : 1;

          const swapsAxes = orientation >= 5 && orientation <= 8;
          const orientedWidth = swapsAxes ? sourceHeight : sourceWidth;
          const orientedHeight = swapsAxes ? sourceWidth : sourceHeight;
          const scale = Math.min(1, maxDimension / Math.max(orientedWidth, orientedHeight));

          const drawWidth = Math.max(1, Math.round(sourceWidth * scale));
          const drawHeight = Math.max(1, Math.round(sourceHeight * scale));

          const canvas = document.createElement('canvas');
          canvas.width = swapsAxes ? drawHeight : drawWidth;
          canvas.height = swapsAxes ? drawWidth : drawHeight;

          const context = canvas.getContext('2d');
          if (!context) {
            reject(new Error('canvas_error'));
            return;
          }

          this.applyExifTransform(context, orientation, drawWidth, drawHeight);
          context.drawImage(image, 0, 0, drawWidth, drawHeight);

          const outputType =
            inputType === 'image/png' || inputType === 'image/webp' || inputType === 'image/jpeg'
              ? inputType
              : 'image/png';

          resolve(canvas.toDataURL(outputType, 0.9));
        } catch {
          reject(new Error('image_error'));
        }
      };

      reader.readAsDataURL(file);
    });
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error ?? new Error('read_error'));
      reader.readAsDataURL(file);
    });
  }

  private normalizePhotoUrl(photo: string | null | undefined): string {
    const value = String(photo ?? '').trim();
    if (!value) {
      return '';
    }

    if (value.startsWith('data:image/')) {
      return value;
    }

    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) {
      return value;
    }

    return '';
  }

  private loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onerror = () => reject(new Error('image_error'));
      image.onload = () => resolve(image);
      image.src = dataUrl;
    });
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error ?? new Error('read_error'));
      reader.readAsArrayBuffer(file);
    });
  }

  private applyExifTransform(
    context: CanvasRenderingContext2D,
    orientation: number,
    width: number,
    height: number
  ): void {
    switch (orientation) {
      case 2:
        context.transform(-1, 0, 0, 1, width, 0);
        break;
      case 3:
        context.transform(-1, 0, 0, -1, width, height);
        break;
      case 4:
        context.transform(1, 0, 0, -1, 0, height);
        break;
      case 5:
        context.transform(0, 1, 1, 0, 0, 0);
        break;
      case 6:
        context.transform(0, 1, -1, 0, height, 0);
        break;
      case 7:
        context.transform(0, -1, -1, 0, height, width);
        break;
      case 8:
        context.transform(0, -1, 1, 0, 0, width);
        break;
      default:
        break;
    }
  }

  private getJpegExifOrientation(buffer: ArrayBuffer): number {
    const view = new DataView(buffer);
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xFFD8) {
      return 1;
    }

    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset, false);
      offset += 2;

      if (marker === 0xFFE1) {
        if (offset + 2 > view.byteLength) {
          return 1;
        }

        const segmentLength = view.getUint16(offset, false);
        const segmentStart = offset + 2;
        if (segmentStart + segmentLength - 2 > view.byteLength) {
          return 1;
        }

        if (view.getUint32(segmentStart, false) !== 0x45786966) {
          return 1;
        }

        const tiffOffset = segmentStart + 6;
        if (tiffOffset + 8 > view.byteLength) {
          return 1;
        }

        const littleEndian = view.getUint16(tiffOffset, false) === 0x4949;
        const firstIfdOffset = view.getUint32(tiffOffset + 4, littleEndian);
        const ifdStart = tiffOffset + firstIfdOffset;
        if (ifdStart + 2 > view.byteLength) {
          return 1;
        }

        const entries = view.getUint16(ifdStart, littleEndian);
        for (let i = 0; i < entries; i += 1) {
          const entryOffset = ifdStart + 2 + (i * 12);
          if (entryOffset + 12 > view.byteLength) {
            return 1;
          }

          const tag = view.getUint16(entryOffset, littleEndian);
          if (tag === 0x0112) {
            const orientation = view.getUint16(entryOffset + 8, littleEndian);
            return orientation >= 1 && orientation <= 8 ? orientation : 1;
          }
        }

        return 1;
      }

      if ((marker & 0xFF00) !== 0xFF00 || marker === 0xFFDA || marker === 0xFFD9) {
        break;
      }

      if (offset + 2 > view.byteLength) {
        break;
      }

      const segmentLength = view.getUint16(offset, false);
      offset += segmentLength;
    }

    return 1;
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    const maybe = err as { error?: { detail?: unknown }; message?: string };
    const detail = maybe?.error?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string; message?: string };
      return first?.msg ?? first?.message ?? fallback;
    }
    if (typeof maybe?.message === 'string' && maybe.message.trim()) {
      return maybe.message;
    }
    return fallback;
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => {
      this.zone.run(() => {
        this.toastMessage = '';
        this.toastType = '';
        this.toastTimer = null;
        this.cdr.detectChanges();
      });
    }, 5000);
  }
}
